import { getAggSafeAppId } from './application.js';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import isUndefined from 'lodash/isUndefined';

function createAppSpecificFieldParam ({ field, appId }) {
    const isAllApps = appId === '*' || isUndefined(appId);
    if (isAllApps) {
        return field;
    }

    const aggSafeAppId = getAggSafeAppId(appId);

    return field.replace(/auto\./, `auto_${aggSafeAppId}.`);
}

function buildAppAutoMetadataBeforeFilterString ({ field, value }) {
    const date = moment(value, { ignoreTimeZone: true }).format(DATE_FORMAT.iso);

    return `${field}<=date("${date}")`;
}

function buildAppAutoMetadataSinceFilterString ({ field, value }) {
    const date = moment(value, { ignoreTimeZone: true }).format(DATE_FORMAT.iso);

    return `${field}>=date("${date}")`;
}

function buildAppAutoMetadataWithinLastFilterString ({ field, count, granularity }) {
    const dateSelection = `dateAdd(startOfPeriod("daily",now()),-${count},"${granularity}")`;

    return `${field}<=now()&&${field}>=${dateSelection}`;
}

function buildAppAutoMetadataNotWithinLastFilterString ({ field, count, granularity }) {
    const dateSelection = `dateAdd(startOfPeriod("daily",now()),-${count},"${granularity}")`;

    return `${field}>=now()||${field}<=${dateSelection}`;
}

function buildAppFirstVisitWithinLastFilterString ({ field, count, granularity }) {
    const dateSelection = `dateAdd(startOfPeriod("daily",now()),-${count},"${granularity}")`;

    return `${field}>=${dateSelection}`;
}

function buildAppFirstVisitNotWithinLastFilterString ({ field, count, granularity }) {
    const dateSelection = `dateAdd(startOfPeriod("daily",now()),-${count},"${granularity}")`;

    return `${field}<=${dateSelection}`;
}

function buildAppAutoMetadataUsedBetweenFilterString ({ field, first, last }) {
    const formattedFirst = moment(first, { ignoreTimeZone: true }).format(DATE_FORMAT.iso);
    const formattedLast = moment(last, { ignoreTimeZone: true })
        .add(1, 'days')
        .format(DATE_FORMAT.iso);

    return `${field}>=date("${formattedFirst}")&&${field}<date("${formattedLast}")`;
}

function buildAppAutoMetadataWithinNextFilterString ({ field, count, granularity }) {
    const dateSelection = `dateAdd(startOfPeriod("daily",now()),${count},"${granularity}")`;

    return `${field}>=now()&&${field}<=${dateSelection}`;
}

function buildAppAutoMetadataNotWithinNextFilterString ({ field, count, granularity }) {
    const dateSelection = `dateAdd(startOfPeriod("daily",now()),${count},"${granularity}")`;

    return `${field}<=now()||${field}>=${dateSelection}`;
}

function buildAppAutoMetadataEmptyFilterString ({ field }) {
    return `isEmpty(${field})`;
}

function buildAppAutoMetadataNotEmptyFilterString ({ field }) {
    return `!isEmpty(${field})`;
}

export function parseAppSpecificAutoMetadataFilterPipeline ({ id, operator, ...params }) {
    const firstVisitIds = [
        'metadata_auto_firstidentifiedvisit',
        'metadata_auto_firstanonymousvisit',
        'metadata_auto_firstvisit',
    ];
    const isFirstVisit = firstVisitIds.includes(id);
    params.field = createAppSpecificFieldParam(params);

    let filterString;

    if (isFirstVisit && operator.includes('withinLast')) {
        switch (operator) {
            case 'withinLast':
                filterString = buildAppFirstVisitWithinLastFilterString(params);
                break;
            case '!withinLast':
                filterString = buildAppFirstVisitNotWithinLastFilterString(params);
                break;
        }
    } else {
        switch (operator) {
            case '<=':
                filterString = buildAppAutoMetadataBeforeFilterString(params);
                break;
            case '>=':
                filterString = buildAppAutoMetadataSinceFilterString(params);
                break;
            case 'withinLast':
                filterString = buildAppAutoMetadataWithinLastFilterString(params);
                break;
            case '!withinLast':
                filterString = buildAppAutoMetadataNotWithinLastFilterString(params);
                break;
            case 'withinNext':
                filterString = buildAppAutoMetadataWithinNextFilterString(params);
                break;
            case '!withinNext':
                filterString = buildAppAutoMetadataNotWithinNextFilterString(params);
                break;
            case 'between':
                filterString = buildAppAutoMetadataUsedBetweenFilterString(params);
                break;
            case 'empty':
                filterString = buildAppAutoMetadataEmptyFilterString(params);
                break;
            case '!empty':
                filterString = buildAppAutoMetadataNotEmptyFilterString(params);
                break;
        }
    }

    return { filter: filterString };
}
